/* WHICH WAY EVERY PIXEL FACES, so the presenter can light it.
 *
 * `src/display.ts` works on the finished 256x240 frame and knows nothing about
 * what any pixel MEANS. That is fine for a vignette and useless for lighting:
 * a brazier should pick out the top of a boulder and leave its far side dark,
 * and you cannot tell a boulder's top from its side by looking at the colour.
 * Guessing relief from brightness is a shader that breaks the day somebody
 * repaints a palette — the same argument `watermask.js` makes, for the same
 * reason.
 *
 * So the game hands the presenter a second buffer, the same shape as the
 * frame, built the way the water mask is:
 *
 *   R = normal x, 0..255 mapping -1..1
 *   G = normal y, 0..255 mapping -1..1   (screen y, DOWN is positive)
 *   B = normal z, 0..255 mapping  0..1   (towards the viewer)
 *
 * A flat pixel is (128, 128, 255). The buffer is cleared to that, so anything
 * this module has no opinion about is simply flat and lights evenly — which
 * is why "everything is covered" is true by construction rather than by
 * remembering to add each new thing.
 *
 * WHERE THE SHAPES COME FROM. Three sources, most exact first:
 *
 *   analytic   a canopy crown is a disc with a known centre and radius, so
 *              its normal is arithmetic, not a guess (see `crownNormals`)
 *   profiled   the ~29 tile kinds each get a height profile below — a dome
 *              for a boulder, a pit for a cave mouth, ripples for water —
 *              and the normal is the slope of that height field
 *   derived    anything unlisted falls through to FLAT, and callers that have
 *              a mask but no profile (sprites) get a dome from the distance
 *              transform of that mask, which is the right shape for a round
 *              body and cheap enough to cache per sprite
 *
 * NOTHING HERE IS ALLOWED TO COST A FRAME. Tiles and crowns are static for as
 * long as a screen is loaded, so they bake once on screen load, exactly like
 * the water mask. Only moving things are touched per frame.
 */
import { T, TS } from './art.ts';

/** Flat, in the encoding above: facing straight at the viewer. */
export const FLAT = [128, 128, 255];

/* HEIGHT PROFILES, one per tile kind.
 *
 * `h(u, v)` takes a position inside the tile, both 0..1, and returns a height
 * in 0..1. The normal is the slope of that surface, computed once per pixel in
 * `bakeTileNormals`, so a profile is a description of a SHAPE and never of a
 * lighting result — which keeps this readable and keeps the lighting in one
 * place.
 *
 * `amp` is how tall the shape stands relative to a tile. It is what decides
 * whether something reads as a boulder you could hide behind or a scuff in the
 * dirt, and it is deliberately small for ground: a floor with visible relief
 * turns into gravel and the player stops being the most legible thing on the
 * screen.
 */
const dome = (u, v) => {
  const dx = (u - 0.5) * 2, dy = (v - 0.5) * 2;
  const d = Math.min(1, Math.hypot(dx, dy));
  return Math.cos(d * Math.PI / 2);            // 1 at the middle, 0 at the rim
};
const pit = (u, v) => 1 - dome(u, v);
const block = (u, v) => {
  // flat on top with a bevel, so a wall lights as a face and an edge rather
  // than as a ball
  const e = Math.min(u, v, 1 - u, 1 - v);
  return Math.min(1, e / 0.22);
};
const ripple = (u, v) => 0.5 + 0.5 * Math.sin((v * 3.2 + u * 0.8) * Math.PI * 2);
const step = (u, v) => Math.floor(v * 4) / 3;  // stairs run down the screen
const spike = (u, v) => {
  const a = Math.abs(((u * 3) % 1) - 0.5) * 2;
  const b = Math.abs(((v * 3) % 1) - 0.5) * 2;
  return 1 - Math.max(a, b);
};

/** { f, amp } per tile kind. Anything absent is flat, and flat is correct for
 *  most ground — see the note on `amp` above. */
export const PROFILE = {
  [T.ROCK]:         { f: dome,   amp: 1.00 },
  [T.RUBBLE]:       { f: dome,   amp: 0.62 },
  [T.PUSH]:         { f: block,  amp: 0.85 },
  [T.NEST]:         { f: dome,   amp: 0.80 },
  // A stack, not a boulder: `block` gives it the flat lit top and the hard
  // shaded side that says somebody set these courses down level.
  [T.CAIRN]:        { f: block,  amp: 0.82 },
  [T.BRAZIER]:      { f: dome,   amp: 0.70 },
  [T.BRAZIER_LIT]:  { f: dome,   amp: 0.70 },
  [T.TREE]:         { f: dome,   amp: 0.55 },  // the crown itself is analytic
  /* A BUSH IS A MOUND, NOT A BED OF NAILS (LS-396, 2026-09-12). `spike` gave every thorn tile a three-by-three grid
   * of pyramids, and the sun lit each one as a wedge: under a thicket on scree the ground wore a lattice of dark
   * wedges on a five-pixel period, which survived every painter being switched off because it was the normal map.
   * The bramble is painted as lumps now (art.ts), so it stands as one dome, a little lower than a tree's. */
  [T.THORN]:        { f: dome,   amp: 0.45 },
  [T.WATER]:        { f: ripple, amp: 0.16 },
  [T.SHALLOW]:      { f: ripple, amp: 0.11 },
  [T.CRACK]:        { f: pit,    amp: 0.45 },
  [T.DEBRIS]:       { f: dome,   amp: 0.22 },
  /* GROUND IS A FIELD, NOT A GRID OF DOMES (LS-201). Moss had a dome per tile
   * and sand a ripple per tile, both tiny -- and tiny was enough: with cast
   * shadows and contact shade in the frame, sixteen-pixel bumps on a lattice
   * read as bubble wrap across every meadow, which is the fault the rocks had
   * already been cured of by shading as one mass. `field: true` says the
   * profile is sampled as a continuous noise over the screen instead of
   * restarting at every tile edge, so the ground has grain and no grid. */
  [T.MOSS]:         { f: dome,   amp: 0.06, field: true },
  [T.TUFT]:         { f: dome,   amp: 0.06, field: true },   // cut moss is the same ground
  [T.SAND]:         { f: ripple, amp: 0.05, field: true },
};

/** A smooth noise over screen pixels, for the ground kinds: two octaves of
 *  hashed value noise, continuous across tile edges by construction. */
function groundNoise(x: number, y: number): number {
  const h = (ix: number, iy: number) => {
    let n = (ix * 374761393 + iy * 668265263) | 0;
    n = (n ^ (n >>> 13)) * 1274126177 | 0;
    return ((n ^ (n >>> 16)) >>> 0) / 4294967296;
  };
  const oct = (sx: number, sy: number) => {
    const ix = Math.floor(sx), iy = Math.floor(sy), fx = sx - ix, fy = sy - iy;
    const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
    const a = h(ix, iy), b = h(ix + 1, iy), c = h(ix, iy + 1), d = h(ix + 1, iy + 1);
    return (a * (1 - u) + b * u) * (1 - v) + (c * (1 - u) + d * u) * v;
  };
  return oct(x / 11, y / 11) * 0.7 + oct(x / 5, y / 5) * 0.3;
}

/** Pack a slope into the RGB encoding. `sx`/`sy` are height differences per
 *  pixel; `z` sets how much relief the slope is allowed to imply. */
function packNormal(d, i, sx, sy, z = 1) {
  let nx = -sx, ny = -sy, nz = z;
  const len = Math.hypot(nx, ny, nz) || 1;
  nx /= len; ny /= len; nz /= len;
  d[i] = Math.round((nx * 0.5 + 0.5) * 255);
  d[i + 1] = Math.round((ny * 0.5 + 0.5) * 255);
  d[i + 2] = Math.round(Math.max(0, nz) * 255);
  d[i + 3] = 255;
}

/**
 * Bake the static normal buffer for a screen: the tile grid, by profile.
 *
 * FRAME-SHAPED, NOT PLAYFIELD-SHAPED, for the reason `buildWaterMask` gives —
 * the shader samples this and the picture with one set of texture coordinates,
 * so a buffer that is short by the HUD's height misaligns every normal on the
 * screen by 48 pixels and lights the world as though it were sliding.
 *
 * @param hudH  pixels of HUD above the playfield
 * @returns a canvas, or null in a headless context (no pixels, no lighting —
 *          lighting is presentation and nothing in the simulation reads it)
 */
/* AND THE LAND ITSELF HAS A SHAPE, which this pass has never known about.
 *
 * Everything below builds relief from what a tile IS — a rock gets a rock's
 * profile, a mass gets a distance field. That is detail at the size of a tile
 * and smaller, and it is why the world has always been lit like a texture: two
 * hills and a valley between them were lit identically, because a grass tile
 * is a grass tile whichever one it is standing on.
 *
 * `elev` is a window on the world's elevation field, one value per tile plus a
 * one-tile border so the gradient at the edge is real rather than clamped. Its
 * gradient tilts the normal across the WHOLE screen, under the per-tile detail
 * rather than instead of it, so a slope reads as a slope and the rock standing
 * on it still reads as a rock.
 *
 * Optional on purpose: `null` gives exactly the old behaviour, and a hollow
 * room has no elevation because it is a room.
 */
/* THE SAME SHAPE, AS A HEIGHT FIELD (LS-201).
 *
 * Vijay: "can we fully test the normal mapping? want to see if ray tracing or
 * dynamic lighting and shadows would work". Normals answer which way a
 * surface faces, which is enough to SHADE it and not enough to SHADOW it: a
 * shadow is a question about what stands between this pixel and the light,
 * and that needs heights.
 *
 * Every profile in `PROFILE` already IS a height field -- the normals above
 * are its derivatives -- so the heights come out of the same table for free,
 * in world pixels, with `amp` scaled by how tall that kind actually stands.
 * A rock is about ten pixels of rock; water ripples are a pixel. Bodies and
 * crowns are stamped over this by the caller.
 *
 * Returned as a Float32Array rather than a canvas: nothing about it is a
 * picture, and a shader that wants it can upload it as a single channel. */
export const TILE_HEIGHT = {
  [T.ROCK]: 11, [T.RUBBLE]: 9, [T.PUSH]: 12,
  [T.NEST]: 9, [T.CAIRN]: 12,
  [T.BRAZIER]: 9, [T.BRAZIER_LIT]: 9, [T.TREE]: 14, [T.THORN]: 10,
  [T.WATER]: 1, [T.SHALLOW]: 1, [T.CLIFF]: 16, [T.STONE]: 15,
  [T.DEBRIS]: 2,
  /* ZERO IS AN ANSWER, NOT AN OMISSION. These kinds are SHAPED -- the relief
   * gives them a profile so the light finds their surface -- and they stand
   * up out of nothing: sand and moss are ground, a mouth and a hole are
   * DENTS, a plate is flush with the floor, a crack is in a wall. A thing
   * that is lit and casts nothing is a real category, and writing it down is
   * how the two tables can be checked against each other at all. */
  [T.SAND]: 0, [T.MOSS]: 0, [T.TUFT]: 0, [T.CRACK]: 0,
};
/** The same field as a canvas, height packed into red, 0..24 world pixels --
 *  which is what a shader can sample. Baked per screen beside the normals. */
/* THE GROUND'S OWN HEIGHT, FROM THE FIELDS (LS-221). Until now the height
 * map held only the props' profiles, so a terrace was a flat tint and a
 * cliff threw no shadow. Each terrace level is `levelH` units, read from the
 * per-pixel level field and blurred a few pixels so a step is a slope; a
 * pixel the rock field calls cliff stands `wallH` above that, so a wall
 * throws a wall's shadow across the lower ground. The props' profiles are
 * laid on top by `bakeHeightCanvas`. Vijay: "i think we can actually
 * visually show slopes between different elevations and shadows/lighting". */
/** THE TERRACES AS THEY ARE, UNBLURRED: each pixel's terrace height with the ramps as slopes and nothing else -- no wall
 *  standing, no rock, no smoothing -- for the perspective camera's march (2026-09-13), which wants a step where the
 *  terrace steps and a plateau that is flat to the last pixel; the blurred field below is for the light. */
export function bakeFieldLevels(levels, cols, rows, hudH = 0, levelH = 8, ramps: any = null): Float32Array | null {
  if (!levels) return null;
  const W = cols * TS, Hs = rows * TS, H = Hs + hudH;
  const out = new Float32Array(W * H);
  for (let ty = 0; ty < rows; ty++) for (let tx = 0; tx < cols; tx++) {
    const isRamp = ramps && ramps.tiles && ramps.tiles[ty] && ramps.tiles[ty][tx] === ramps.kind;
    let dir = 0, lo = 0, hi = 0;
    if (isRamp) {
      const lv = (x, y) => (ramps.levelOf ? ramps.levelOf(x, y) : 0) | 0;
      const n = lv(tx, ty - 1), s2 = lv(tx, ty + 1), w = lv(tx - 1, ty), e = lv(tx + 1, ty);
      if (n !== s2 && Math.abs(n - s2) >= Math.abs(e - w)) { dir = n > s2 ? 1 : 2; lo = Math.min(n, s2); hi = Math.max(n, s2); }
      else if (e !== w) { dir = w > e ? 3 : 4; lo = Math.min(e, w); hi = Math.max(e, w); }
    }
    for (let py = 0; py < TS; py++) for (let px = 0; px < TS; px++) {
      const i = (ty * TS + py) * W + tx * TS + px, o = (hudH + ty * TS + py) * W + tx * TS + px;
      if (dir) { const t = dir === 1 ? 1 - (py + 0.5) / TS : dir === 2 ? (py + 0.5) / TS : dir === 3 ? 1 - (px + 0.5) / TS : (px + 0.5) / TS; out[o] = (lo + (hi - lo) * t) * levelH; }
      else out[o] = (levels[i] | 0) * levelH;
    }
  }
  return out;
}
export function bakeFieldHeights(levels, rock, cols, rows, hudH = 0, levelH = 8, wallH = 4, rockKind = -1, ramps: any = null, beyond: ((px: number, py: number) => number) | null = null): Float32Array | null {
  /* `beyond(px, py)` is the terrace level of a pixel outside the screen (the neighbour's), so the blur runs through the
   * seam: a terrace that ends on a screen's edge was a vertical step there and a slope everywhere else, and the shader
   * laid a ruled line of shade along the step (2026-09-12; Vijay's coast shot). */
  if (!levels) return null;
  const W = cols * TS, Hs = rows * TS, H = Hs + hudH;
  const base = new Float32Array(W * Hs);
  for (let i = 0; i < W * Hs; i++) base[i] = (levels[i] | 0) * levelH;
  /* A RAMP IS A SLOPE. `ramps` is { tiles, kind, levelOf }: a cell of that kind
   * climbs from the level on its low side to the level on its high side across
   * its sixteen pixels, so the stair reads as a slope between the terraces
   * rather than a flat shelf with a step at each end (LS-221). */
  if (ramps && ramps.tiles) {
    const { tiles, kind, levelOf } = ramps;
    for (let ty = 0; ty < rows; ty++) for (let tx = 0; tx < cols; tx++) {
      if (!tiles[ty] || tiles[ty][tx] !== kind) continue;
      const lv = (x, y) => (levelOf ? levelOf(x, y) : 0) | 0;
      const n = lv(tx, ty - 1), s2 = lv(tx, ty + 1), w = lv(tx - 1, ty), e = lv(tx + 1, ty);
      let dir = 0, lo = 0, hi = 0;
      if (n !== s2 && Math.abs(n - s2) >= Math.abs(e - w)) { dir = n > s2 ? 1 : 2; lo = Math.min(n, s2); hi = Math.max(n, s2); }
      else if (e !== w) { dir = w > e ? 3 : 4; lo = Math.min(e, w); hi = Math.max(e, w); }
      if (!dir) continue;
      for (let py = 0; py < TS; py++) for (let px = 0; px < TS; px++) {
        const t = dir === 1 ? 1 - (py + 0.5) / TS : dir === 2 ? (py + 0.5) / TS : dir === 3 ? 1 - (px + 0.5) / TS : (px + 0.5) / TS;   // 1 at the high side
        base[(ty * TS + py) * W + tx * TS + px] = (lo + (hi - lo) * t) * levelH;
      }
    }
  }
  const tmp = new Float32Array(W * Hs);
  const blur = (src, dst) => {   // a separable box blur, radius two, run twice: a step becomes a slope five or six pixels wide
    // Interior taps are always in bounds. Keep the original addition order,
    // including float32 storage between passes, so the lighting stays exact.
    for (let y = 0; y < Hs; y++) for (let x = 0; x < W; x++) {
      const i = y * W + x;
      if (x >= 2 && x < W - 2) tmp[i] = (src[i - 2] + src[i - 1] + src[i] + src[i + 1] + src[i + 2]) / 5;
      else {
        let sum = 0, count = 0;
        for (let k = -2; k <= 2; k++) { const xx = x + k; if (xx < 0 || xx >= W) { if (beyond) { sum += beyond(xx, y) * levelH; count++; } continue; } sum += src[y * W + xx]; count++; }
        tmp[i] = sum / count;
      }
    }
    for (let y = 0; y < Hs; y++) for (let x = 0; x < W; x++) {
      const i = y * W + x;
      if (y >= 2 && y < Hs - 2) dst[i] = (tmp[i - 2 * W] + tmp[i - W] + tmp[i] + tmp[i + W] + tmp[i + 2 * W]) / 5;
      else {
        let sum = 0, count = 0;
        for (let k = -2; k <= 2; k++) { const yy = y + k; if (yy < 0 || yy >= Hs) { if (beyond) { sum += beyond(x, yy) * levelH; count++; } continue; } sum += tmp[yy * W + x]; count++; }
        dst[i] = sum / count;
      }
    }
  };
  const sl = new Float32Array(W * Hs);
  blur(base, sl); blur(sl, base);
  const out = new Float32Array(W * H);
  for (let y = 0; y < Hs; y++) for (let x = 0; x < W; x++) {
    let h = base[y * W + x];
    if (rock && rock[y * W + x] === rockKind) h += wallH;
    out[(y + hudH) * W + x] = h;
  }
  return out;
}

export function bakeHeightCanvas(tiles, cols, rows, hudH = 0, extra: Float32Array | null = null, terrain: Float32Array | null = null) {
  if (typeof document === 'undefined') return null;
  const W = cols * TS, H = rows * TS + hudH;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const g = c.getContext('2d');
  const img = g && g.createImageData && g.createImageData(W, H);
  if (!g || !img) return null;
  const h = bakeTileHeights(tiles, cols, rows, hudH);
  if (extra) for (let i = 0; i < W * H; i++) h[i] = Math.max(h[i], extra[i] || 0);   // the props' profiles over the ground's own height (LS-221)
  for (let i = 0; i < W * H; i++) {
    const v = Math.max(0, Math.min(255, Math.round(h[i] / 24 * 255)));
    // green: the GROUND's own height alone (terraces and walls, no prop), for the perspective camera's march (2026-09-13):
    // a rock or a nest under that march grew a face and a body a smear; only the land is terrain
    const src = terrain || extra; const t = src ? Math.max(0, Math.min(255, Math.round((src[i] || 0) / 24 * 255))) : 0;
    img.data[i * 4] = v; img.data[i * 4 + 1] = t; img.data[i * 4 + 3] = 255;
  }
  g.putImageData(img, 0, 0);
  return c;
}

export function bakeTileHeights(tiles, cols, rows, hudH = 0): Float32Array {
  const W = cols * TS, H = rows * TS + hudH;
  const out = new Float32Array(W * H);
  for (let ty = 0; ty < rows; ty++) {
    for (let tx = 0; tx < cols; tx++) {
      const kind = tiles[ty][tx];
      const p = PROFILE[kind];
      const tall = TILE_HEIGHT[kind];
      if (!p || !tall) continue;
      for (let py = 0; py < TS; py++) {
        for (let px = 0; px < TS; px++) {
          const u = (px + 0.5) / TS, v = (py + 0.5) / TS;
          const h = Math.max(0, p.f(u, v)) * p.amp * tall;
          out[(ty * TS + py + hudH) * W + tx * TS + px] = h;
        }
      }
    }
  }
  return out;
}

/** Where the light cannot reach: march every pixel toward the light through
 *  the height field and see whether anything stands in the way.
 *
 *  This is a screen-space ray march, which is the honest version of the
 *  question at this scale: the world IS a height field seen from above, so a
 *  ray from a pixel to the light is a line across that field, and a taller
 *  pixel on the way blocks it. `dir` is the direction the light comes FROM in
 *  screen pixels; `slope` is how fast the ray climbs per pixel travelled --
 *  a low sun casts long shadows and a high one casts almost none. */
export function shadowMask(height: Float32Array, w: number, h: number,
  dirX: number, dirY: number, slope = 0.55, steps = 24): Uint8Array {
  const out = new Uint8Array(w * h);
  const len = Math.hypot(dirX, dirY) || 1;
  const sx = dirX / len, sy = dirY / len;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const start = height[y * w + x];
      let shaded = 0;
      for (let i = 1; i <= steps; i++) {
        const px2 = Math.round(x + sx * i), py2 = Math.round(y + sy * i);
        if (px2 < 0 || py2 < 0 || px2 >= w || py2 >= h) break;
        const ray = start + i * slope;
        const blocker = height[py2 * w + px2];
        if (blocker > ray + 0.5) { shaded = Math.min(255, Math.round((blocker - ray) * 26)); break; }
      }
      out[y * w + x] = shaded;
    }
  }
  return out;
}

/** The normal bake as bare bytes, for a check that has no canvas to draw on:
 *  the same loop as `bakeTileNormals` without the ImageData round trip. */
export function bakeTileNormalsData(tiles, cols, rows, hudH = 0): Uint8ClampedArray {
  const W = cols * TS, H = rows * TS + hudH;
  const d = new Uint8ClampedArray(W * H * 4);
  for (let i = 0; i < d.length; i += 4) { d[i] = FLAT[0]; d[i + 1] = FLAT[1]; d[i + 2] = FLAT[2]; d[i + 3] = 255; }
  for (let ty = 0; ty < rows; ty++) {
    for (let tx = 0; tx < cols; tx++) {
      const kind = tiles[ty][tx];
      const p = PROFILE[kind];
      if (!p) continue;
      for (let py = 0; py < TS; py++) {
        for (let px = 0; px < TS; px++) {
          const x = tx * TS + px, y = ty * TS + py + hudH;
          if ((p as any).field) {
            const gx = x, gy = y - hudH;
            const hx = (groundNoise(gx + 1, gy) - groundNoise(gx - 1, gy)) * p.amp * 6;
            const hy = (groundNoise(gx, gy + 1) - groundNoise(gx, gy - 1)) * p.amp * 6;
            packNormal(d, (y * W + x) * 4, hx * 4, hy * 4);
            continue;
          }
          const u = (px + 0.5) / TS, v = (py + 0.5) / TS, e = 1 / TS;
          const hx = (p.f(Math.min(1, u + e), v) - p.f(Math.max(0, u - e), v)) * p.amp;
          const hy = (p.f(u, Math.min(1, v + e)) - p.f(u, Math.max(0, v - e))) * p.amp;
          packNormal(d, (y * W + x) * 4, hx * 4, hy * 4);
        }
      }
    }
  }
  return d;
}

export function bakeTileNormals(tiles, cols, rows, hudH = 0, elev: any = null, heights: Float32Array | null = null) {
  const W = cols * TS, H = rows * TS + hudH;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const g = c.getContext('2d');
  const img = g.createImageData && g.createImageData(W, H);
  if (!img || !img.data) return null;
  const d = img.data;
  // flat everywhere first, so a kind with no profile is lit evenly instead of
  // being lit by whatever was left in the buffer
  for (let i = 0; i < d.length; i += 4) {
    d[i] = FLAT[0]; d[i + 1] = FLAT[1]; d[i + 2] = FLAT[2]; d[i + 3] = 255;
  }
  /* THE LAND'S OWN SLOPE, BILINEAR ACROSS THE TILE so a hillside is a hillside
   * and not sixteen steps. `elev.get(tx, ty)` reads the window including its
   * one-tile border, which is what makes the gradient at a screen edge agree
   * with the screen next door. */
  if (heights) {
    // the field's own slopes (LS-221): the gradient of the ground's height, in the units of `bakeFieldHeights`
    const GAIN = 190 / 8;
    const K = GAIN * TS / 2;
    for (let py = 0; py < rows * TS; py++) {
      const row = (py + hudH) * W;
      const above = (Math.max(0, py - 1) + hudH) * W;
      const below = (Math.min(rows * TS - 1, py + 1) + hudH) * W;
      for (let px = 0; px < W; px++) {
        const gx = (heights[row + Math.min(W - 1, px + 1)] - heights[row + Math.max(0, px - 1)]) * K;
        const gy = (heights[below + px] - heights[above + px]) * K;
        if (!gx && !gy) continue;
        const i = ((py + hudH) * W + px) * 4;
        d[i] = Math.max(0, Math.min(255, d[i] - gx));
        d[i + 1] = Math.max(0, Math.min(255, d[i + 1] - gy));
      }
    }
  } else if (elev) {
    /* THE LAND TILTS THE NORMAL PER PIXEL, NOT PER TILE (LS-218). This took
     * the elevation difference across a tile's neighbours and tilted the
     * whole tile by it, so every terrace step lit or shaded a sixteen-pixel
     * band with square corners -- the loudest grid on a flat screen, and in
     * no 2D layer at all (it lives in the lighting). The elevation is read
     * between tile centres now, bilinear, with the sample point wobbled by a
     * few pixels of continuous noise, and the gradient is the pixel's own:
     * a step is a soft ramp that meanders, a corner is round. */
    const GAIN = 190;
    const E = new Float32Array((cols + 2) * (rows + 2));
    for (let ty = -1; ty <= rows; ty++) for (let tx = -1; tx <= cols; tx++) E[(ty + 1) * (cols + 2) + tx + 1] = elev.get(tx, ty) || 0;
    const eAt = (fx, fy) => {   // fx, fy in tile units, centre-based
      const cx = Math.max(-1, Math.min(cols, fx)), cy = Math.max(-1, Math.min(rows, fy));
      const x0 = Math.floor(cx), y0 = Math.floor(cy), u = cx - x0, v = cy - y0;
      const at = (x, y) => E[(Math.max(-1, Math.min(rows, y)) + 1) * (cols + 2) + Math.max(-1, Math.min(cols, x)) + 1];
      return (at(x0, y0) * (1 - u) + at(x0 + 1, y0) * u) * (1 - v) + (at(x0, y0 + 1) * (1 - u) + at(x0 + 1, y0 + 1) * u) * v;
    };
    const WOB = 3.5;
    const sample = (x, y) => {
      const wx = x + (groundNoise(x * 0.11 + 40, y * 0.11) - 0.5) * 2 * WOB;
      const wy = y + (groundNoise(x * 0.11, y * 0.11 + 90) - 0.5) * 2 * WOB;
      return eAt(wx / TS - 0.5, wy / TS - 0.5);
    };
    let any = false;
    for (let i = 1; i < E.length && !any; i++) if (E[i] !== E[0]) any = true;
    if (any) {
      const K = GAIN * TS / 2;   // a central difference over two pixels, scaled so a full step over a tile tilts as the old tile band did
      for (let py = 0; py < rows * TS; py++) {
        for (let px = 0; px < W; px++) {
          const gx = (sample(px + 1, py) - sample(px - 1, py)) * K, gy = (sample(px, py + 1) - sample(px, py - 1)) * K;
          if (!gx && !gy) continue;
          const i = ((py + hudH) * W + px) * 4;
          d[i] = Math.max(0, Math.min(255, d[i] - gx));
          d[i + 1] = Math.max(0, Math.min(255, d[i + 1] - gy));
        }
      }
    }
  }
  /* A MASS IS ONE SHAPE, NOT A GRID OF BALLS.
   *
   * `PROFILE` is a per-tile height field and it never looks at a neighbour —
   * so a screen with 126 rock tiles got 126 copies of the same dome, while
   * `paintMass` in `canopy.ts` draws that rock as a single off-lattice
   * formation. Picture and shape disagreed on 18.9% of all wild tiles, and
   * measured on presented pixels the relief pass made the 16px lattice **55%
   * more visible** on the commonest obstacle in the wilds: tile-locked
   * luminance range 0.0752 -> 0.1169. At 5x it read as bubble wrap.
   *
   * It is the same fault `game.ts` already refuses one layer up, where boulder
   * crowns are filtered out of `crownNormals` because "putting a dome on the
   * picture where the picture has no dome is the one thing this pass must
   * never do".
   *
   * So the masses get a height field built from the DISTANCE TRANSFORM of the
   * whole mass — deep inside is high, the rim falls away — which is the same
   * number `canopy.ts` shades the formation from, and therefore the same
   * shape. `distanceField` is exported for exactly this and had no caller.
   */
  const MASS = new Set([T.ROCK, T.TREE]);
  const massAt = new Uint8Array(W * H);
  let anyMass = false;
  for (let ty = 0; ty < rows; ty++) {
    for (let tx = 0; tx < cols; tx++) {
      if (!MASS.has(tiles[ty][tx])) continue;
      anyMass = true;
      for (let py = 0; py < TS; py++) {
        const y = ty * TS + py + hudH;
        for (let px = 0; px < TS; px++) massAt[y * W + tx * TS + px] = 1;
      }
    }
  }
  if (anyMass) {
    const dist = distanceField(massAt, W, H);
    let max = 0;
    for (let i = 0; i < dist.length; i++) if (dist[i] > max) max = dist[i];
    if (max > 0) {
      const at = (x: number, y: number) => (x < 0 || y < 0 || x >= W || y >= H)
        ? 0 : dist[y * W + x] / max;
      for (let y = 0; y < H; y++) {
        for (let x = 0; x < W; x++) {
          if (!massAt[y * W + x]) continue;
          /* The slope of the mass, not of a tile. `* 2.6` is the same order as
           * a dome's `amp` so a boulder still stands as tall as it did — what
           * changed is that it is now ONE boulder. */
          const hx = (at(x + 1, y) - at(x - 1, y)) * 2.6;
          const hy = (at(x, y + 1) - at(x, y - 1)) * 2.6;
          packNormal(d, (y * W + x) * 4, hx * 4, hy * 4);
        }
      }
    }
  }

  for (let ty = 0; ty < rows; ty++) {
    for (let tx = 0; tx < cols; tx++) {
      const kind = tiles[ty][tx];
      if (MASS.has(kind)) continue;          // shaped above, as one formation
      const p = PROFILE[kind];
      if (!p) continue;
      for (let py = 0; py < TS; py++) {
        for (let px = 0; px < TS; px++) {
          const x = tx * TS + px, y = ty * TS + py + hudH;
          if ((p as any).field) {
            // the ground's grain: a continuous field, so no tile edge shows
            const gx = x, gy = y - hudH;
            const hx = (groundNoise(gx + 1, gy) - groundNoise(gx - 1, gy)) * p.amp * 6;
            const hy = (groundNoise(gx, gy + 1) - groundNoise(gx, gy - 1)) * p.amp * 6;
            packNormal(d, (y * W + x) * 4, hx * 4, hy * 4);
            continue;
          }
          const u = (px + 0.5) / TS, v = (py + 0.5) / TS;
          // central differences on the profile, in tile space
          const e = 1 / TS;
          const hx = (p.f(Math.min(1, u + e), v) - p.f(Math.max(0, u - e), v)) * p.amp;
          const hy = (p.f(u, Math.min(1, v + e)) - p.f(u, Math.max(0, v - e))) * p.amp;
          packNormal(d, (y * W + x) * 4, hx * 4, hy * 4);
        }
      }
    }
  }
  g.putImageData(img, 0, 0);
  return c;
}

/**
 * Draw canopy crowns into an already-baked normal buffer.
 *
 * A crown is a disc of known centre and radius, so this is the one case where
 * the normal is arithmetic and exactly right: at distance `r` from the centre
 * of a hemisphere of radius `R`, the surface points outward by `r/R`. No
 * sobel, no guessing from colour, and it lands on the same pixels the crown
 * is painted on because it uses the same numbers `drawCanopy` does.
 *
 * `squash` matches the spec's, so a flattened thicket lights flattened.
 */
export function crownNormals(canvas, baked, hudH = 0) {
  if (!canvas || !baked) return canvas;
  const g = canvas.getContext('2d');
  const W = canvas.width, H = canvas.height;
  const img = g.getImageData && g.getImageData(0, 0, W, H);
  if (!img || !img.data) return canvas;
  const d = img.data;
  for (const layer of baked) {
    const sq = (layer.spec && layer.spec.squash) || 1;
    /* A LAYER WITHOUT CROWNS IS A LAYER, NOT A CRASH. The shape was guaranteed
     * by the caller — `game.js` filters `canopyPlan`'s output by
     * `spec.kind` — which means this function was safe only for as long as
     * every caller remembered. Guaranteed here instead. */
    if (!layer || !Array.isArray(layer.crowns)) continue;
    for (const c of layer.crowns) {
      const r = c.r;
      const x0 = Math.max(0, Math.floor(c.x - r)), x1 = Math.min(W - 1, Math.ceil(c.x + r));
      const y0 = Math.max(0, Math.floor(c.y * 1 - r * sq)), y1 = Math.min(H - 1, Math.ceil(c.y + r * sq));
      for (let y = y0; y <= y1; y++) {
        for (let x = x0; x <= x1; x++) {
          const dx = (x + 0.5 - c.x) / r;
          const dy = (y + 0.5 - c.y) / (r * sq);
          const q = dx * dx + dy * dy;
          if (q > 1) continue;
          const yy = y + hudH;
          if (yy < 0 || yy >= H) continue;
          const nz = Math.sqrt(1 - q);
          const i = (yy * W + x) * 4;
          d[i] = Math.round((dx * 0.5 + 0.5) * 255);
          d[i + 1] = Math.round((dy * 0.5 + 0.5) * 255);
          d[i + 2] = Math.round(nz * 255);
          d[i + 3] = 255;
        }
      }
    }
  }
  g.putImageData(img, 0, 0);
  return canvas;
}

/**
 * A dome normal image for a sprite, from its own mask.
 *
 * Sprites are drawn as runs of `fillRect` and there is no height information
 * anywhere in them, so the shape has to come from the silhouette. A distance
 * transform gives how far each pixel is from the outside of the body; feeding
 * that through the same hemisphere arithmetic as a crown makes a creature read
 * as rounded, which is right for every body in this game and much better than
 * the alternative of leaving them flat while the world around them has relief.
 *
 * Cache the result per sprite — this is the only thing here that would cost a
 * frame if it ran per frame.
 *
 * @param mask  w*h array, truthy where the sprite is solid
 */
export function domeFromMask(mask, w, h) {
  const dist = distanceField(mask, w, h);
  return domeFromField(dist, mask, w, h);
}

/**
 * How far every solid pixel of a mask is from the outside of it.
 *
 * Exported because it is the same question a rock formation asks: the depth
 * of a mass IS its height, so `canopy.js` shades a scree field from this and
 * takes its normals from the identical numbers rather than from a second
 * approximation that would drift out of agreement with the picture.
 */
export function distanceField(mask, w, h, openEdges = true) {
  const dist = new Float32Array(w * h);
  // TWO-PASS CHAMFER, AND BOTH HALVES OF EACH MASK.
  //
  // The forward pass has to look at all four already-settled neighbours —
  // (-1,0), (-1,-1), (0,-1) AND (+1,-1) — and the backward pass at their four
  // mirrors. Dropping the diagonal that leans the other way is the classic
  // way to get this wrong, and it is not subtle: measured on a 14px disc, the
  // left and right halves of the same body disagreed by up to 41/128 of a
  // normal, so every creature in the game would have been lit as though tilted
  // towards the bottom-right whatever the light was doing.
  const INF = 1e9;
  const D = 1.41421356;
  for (let i = 0; i < w * h; i++) dist[i] = mask[i] ? INF : 0;
  // WHAT THE EDGE OF THE ARRAY MEANS IS A DECISION, NOT A DETAIL.
  //
  // The passes below only ever propagate away from an EMPTY pixel, so a mask
  // with no empty pixels in it never gets seeded at all and every distance
  // stays at infinity — which is not a corner case here, it is a rock mass
  // that reaches the edge of the screen.
  //
  //   openEdges  the array edge is outside, so a body that runs off it is cut
  //              there. Right for a sprite: it really does end at its box.
  //   !openEdges the array edge is more of the same, so the mass stays thick
  //              where it leaves the screen. Right for terrain, which
  //              continues onto the next screen — cutting it here would put a
  //              seam of thin, pale rock down every screen border.
  const edgeSeed = openEdges ? 1 : INF;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (!dist[i]) continue;
      let best = dist[i];
      best = Math.min(best, x > 0 ? dist[i - 1] + 1 : edgeSeed);
      best = Math.min(best, y > 0 ? dist[i - w] + 1 : edgeSeed);
      if (x > 0 && y > 0) best = Math.min(best, dist[i - w - 1] + D);
      if (x < w - 1 && y > 0) best = Math.min(best, dist[i - w + 1] + D);
      dist[i] = best;
    }
  }
  for (let y = h - 1; y >= 0; y--) {
    for (let x = w - 1; x >= 0; x--) {
      const i = y * w + x;
      if (!dist[i]) continue;
      let best = dist[i];
      best = Math.min(best, x < w - 1 ? dist[i + 1] + 1 : edgeSeed);
      best = Math.min(best, y < h - 1 ? dist[i + w] + 1 : edgeSeed);
      if (x < w - 1 && y < h - 1) best = Math.min(best, dist[i + w + 1] + D);
      if (x > 0 && y < h - 1) best = Math.min(best, dist[i + w - 1] + D);
      dist[i] = best;
    }
  }
  // ...and if nothing seeded it at all (a solid mask with closed edges) there
  // is no relief information to be had; flat is the honest answer.
  let fin = 0;
  for (let i = 0; i < w * h; i++) if (dist[i] < INF && dist[i] > fin) fin = dist[i];
  if (!fin) fin = 1;
  for (let i = 0; i < w * h; i++) if (dist[i] >= INF) dist[i] = fin;
  return dist;
}

/** Normals from an already-computed field, so a caller that needs both the
 *  field and the normals pays for the transform once. */
export function domeFromField(dist, mask, w, h) {
  let max = 0;
  for (let i = 0; i < w * h; i++) if (dist[i] > max) max = dist[i];
  const out = new Uint8ClampedArray(w * h * 4);
  if (!max) return out;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      const o = i * 4;
      if (!mask[i]) { out[o + 3] = 0; continue; }
      // height rises towards the middle of the body; slope from its neighbours
      const at = (xx, yy) => {
        if (xx < 0 || yy < 0 || xx >= w || yy >= h) return 0;
        return dist[yy * w + xx] / max;
      };
      const sx = at(x + 1, y) - at(x - 1, y);
      const sy = at(x, y + 1) - at(x, y - 1);
      packNormal(out, o, sx * 2.2, sy * 2.2);
    }
  }
  return out;
}

/* ---------------------------------------------------------------------------
 * THE LIGHTING ITSELF, as the presenter runs it.
 *
 * Kept next to the normals it consumes rather than buried in `display.js`,
 * for the reason the uniform-name list in that file gives: a shader and the
 * names its JS binds drift apart the moment they live in different files.
 *
 * PIXELATED IS A DECISION, NOT A SIDE EFFECT. The present shader samples the
 * picture with a sharp-bilinear filter at OUTPUT resolution — that is what
 * stops the upscale shimmering. If the lighting were computed at that same
 * resolution, a brazier's pool would have a smooth analogue edge sliding
 * across a grid of hard pixels, which is the one thing that would make this
 * look like a filter laid over the game instead of part of it.
 *
 * So the light is computed at the SOURCE texel: the coordinate is snapped to
 * the middle of the game pixel it falls in before anything is sampled or
 * measured, and the falloff is quantised into BANDS steps. The result is that
 * every edge in the lighting lands exactly on a 256x240 pixel boundary and
 * moves in whole-pixel jumps, like the art does.
 */

/* THE SHADER ITSELF LIVES IN `display.js`.
 *
 * It used to live here — `LIGHT_GLSL`, `LIGHT_UNIFORMS`, `BANDS` and
 * `MAX_LIGHTS`, exported to be spliced into the present shader. That put a
 * presentation concern in the module that knows about TILES, and it would
 * have forced `display.js` — which has no imports at all, on purpose, because
 * it "knows nothing about what any pixel MEANS" — to import this file and
 * `art.js` behind it.
 *
 * The split water already uses is the right one and this now matches it:
 * `watermask.js` BUILDS a buffer out of tile knowledge and `display.js` owns
 * the shader that reads it. So this file bakes normals and stops there.
 */
/* THE RELIEF TUNING LIVES WITH THE PRESENTER, for the same reason the shader
 * does: how strong the light is, is a display decision, and this file's job
 * ends at describing what shape the world is. See RELIEF_DEFAULT in
 * `display.js`. */
